import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, Image as ImageIcon, X, Mic, MicOff } from "lucide-react";
import { useSpeechRecognitionHook } from "../hooks/useSpeechRecognition";

interface ChatInputProps {
  onSendMessage: (message: string, image?: File | null) => void;
  isLoading: boolean;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  disabled = false,
}) => {
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [baseMessage, setBaseMessage] = useState<string>("");
  const startTranscriptRef = useRef<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Speech recognition hook
  const {
    isListening,
    transcript,
    error: speechError,
    isSupported: isSpeechSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognitionHook();

  // Allowed image types
  const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Track transcript start point when listening begins
  useEffect(() => {
    if (isListening) {
      // When listening starts, capture the current transcript as the baseline
      // This ensures we only track NEW transcript from this point forward
      startTranscriptRef.current = transcript || "";
    } else {
      // Clear baseline when not listening
      startTranscriptRef.current = "";
    }
  }, [isListening]); // Only run when isListening changes

  // Update message when transcript changes
  useEffect(() => {
    if (isListening && transcript !== undefined) {
      // Calculate only the new transcript portion (difference from when we started)
      const baseline = startTranscriptRef.current || "";
      const newTranscript = baseline
        ? transcript.replace(baseline, "").trim()
        : transcript.trim();
      
      // Combine base message with new transcript
      const newMessage = baseMessage && newTranscript
        ? `${baseMessage} ${newTranscript}`.trim()
        : baseMessage || newTranscript;
      
      if (newMessage !== message) {
        setMessage(newMessage);
      }
    }
  }, [transcript, isListening, baseMessage, message]);

  // Stop listening when sending message or when component is disabled
  useEffect(() => {
    if ((isLoading || disabled) && isListening) {
      stopListening();
    }
  }, [isLoading, disabled, isListening, stopListening]);

  // Process image file (used by both file selection and paste)
  const processImageFile = (file: File) => {
    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("Please select a valid image file (PNG, JPEG, WebP, or GIF)");
      return;
    }

    // Validate file size
    if (file.size > MAX_SIZE) {
      alert("Image size must be less than 10MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setSelectedImage(file);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
  };

  // Handle paste event for images
  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Don't interfere if user is selecting text or if component is disabled
    if (disabled || isLoading) return;

    const clipboardData = e.clipboardData;
    const items = clipboardData.items;

    // Check if clipboard contains image data
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the item is an image
      if (item.type.indexOf("image") !== -1) {
        e.preventDefault(); // Prevent default paste behavior
        
        const blob = item.getAsFile();
        if (!blob) return;

        // Determine file extension based on MIME type
        let extension = "png";
        const mimeType = blob.type || item.type;
        if (mimeType.includes("jpeg") || mimeType.includes("jpg")) {
          extension = "jpg";
        } else if (mimeType.includes("webp")) {
          extension = "webp";
        } else if (mimeType.includes("gif")) {
          extension = "gif";
        }

        // Convert blob to File with a proper name and type
        const file = new File([blob], `pasted-image-${Date.now()}.${extension}`, {
          type: mimeType || "image/png",
        });

        processImageFile(file);
        return; // Process only the first image found
      }
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((message.trim() || selectedImage) && !isLoading && !disabled) {
      // Stop listening if active
      if (isListening) {
        stopListening();
      }
      onSendMessage(message.trim(), selectedImage);
      setMessage("");
      setBaseMessage("");
      startTranscriptRef.current = "";
      resetTranscript();
      setSelectedImage(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle microphone toggle
  const handleMicToggle = () => {
    if (isListening) {
      // Stop listening - keep the current message as is
      stopListening();
      // Update base message to current message state for next session
      setBaseMessage(message.trim());
      // Reset transcript reference
      startTranscriptRef.current = "";
    } else {
      // Start listening
      // Save current message as base if it exists
      const currentMessage = message.trim();
      if (currentMessage) {
        setBaseMessage(currentMessage);
      } else {
        setBaseMessage("");
      }
      
      // Reset transcript completely before starting new session
      resetTranscript();
      // The useEffect will capture the transcript baseline when isListening becomes true
      
      // Start listening - transcript will start fresh from empty
      startListening();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-xl border-t border-neutral-200/50 dark:border-neutral-700/50">
      <form onSubmit={handleSubmit} className="p-3">
        <div className="w-full">
          {/* Image Preview */}
          {imagePreview && selectedImage && (
            <div className="mb-3 relative inline-block">
              <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-[200px] rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                  aria-label="Remove image"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {selectedImage.name} ({formatFileSize(selectedImage.size)})
              </div>
            </div>
          )}

          <div className="relative">
            <div className="relative bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-2xl shadow-sm focus-within:shadow-md transition-all duration-200 overflow-hidden">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Ask a question about your construction project or safety protocols..."
                className="w-full px-4 py-3 pr-28 resize-none focus:outline-none focus:ring-0 disabled:bg-neutral-50 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed transition-all duration-200 text-neutral-700 dark:text-neutral-200 placeholder-neutral-400 dark:placeholder-neutral-500 bg-transparent border-0 m-0"
                rows={1}
                disabled={disabled || isLoading}
              />

              {/* Speech Recognition Button */}
              {isSpeechSupported && (
                <div className="absolute bottom-3 right-12">
                  <button
                    type="button"
                    onClick={handleMicToggle}
                    disabled={disabled || isLoading}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 relative ${
                      isListening
                        ? "bg-red-500 dark:bg-red-600 text-white shadow-lg shadow-red-500/50"
                        : "text-neutral-500 dark:text-neutral-400 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    aria-label={isListening ? "Stop recording" : "Start voice input"}
                    title={isListening ? "Stop recording" : "Start voice input"}
                  >
                    {isListening ? (
                      <>
                        <MicOff size={18} className="relative z-10" />
                        <div className="absolute inset-0 rounded-xl bg-red-500 dark:bg-red-600 animate-ping opacity-75"></div>
                      </>
                    ) : (
                      <Mic size={18} />
                    )}
                  </button>
                </div>
              )}

              {/* Image Upload Button */}
              <div className={`absolute bottom-3 ${isSpeechSupported ? "right-20" : "right-12"}`}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={disabled || isLoading}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isLoading}
                  className="w-8 h-8 text-neutral-500 dark:text-neutral-400 hover:text-orange-500 dark:hover:text-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  aria-label="Upload image"
                >
                  <ImageIcon size={18} />
                </button>
              </div>

              {/* Send Button */}
              <div className="absolute bottom-3 right-3">
                <button
                  type="submit"
                  disabled={(!message.trim() && !selectedImage) || isLoading || disabled}
                  className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 disabled:from-neutral-300 dark:disabled:from-neutral-600 disabled:to-neutral-400 dark:disabled:to-neutral-500 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
                >
                  {isLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Speech Error Message */}
          {speechError && (
            <div className="mt-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                <MicOff size={14} />
                <span>{speechError}</span>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
